/**
 * Custom Wishlist Component
 * Renders theme product cards for Swish wishlist items
 * Supports shareable wishlists via ?list= parameter
 */
if (!customElements.get('custom-wishlist')) {
  customElements.define(
    'custom-wishlist',
    class CustomWishlist extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.loadingEl = this.querySelector('[data-wishlist-loading]');
        this.emptyEl = this.querySelector('[data-wishlist-empty]');
        this.gridEl = this.querySelector('[data-wishlist-grid]');
        this.countEl = this.querySelector('[data-wishlist-count]');
        this.shareBtn = this.querySelector('[data-wishlist-share]');
        this.imageRatio = this.dataset.imageRatio || '';
        this.wishlistId = null; // Will be set after loading
        this.isSharedView = false; // True if viewing someone else's wishlist
        this.roomsEl = this.querySelector('[data-wishlist-rooms]');
        this.roomsGrid = this.querySelector('[data-wishlist-rooms-grid]');
        this.workerUrl = (this.dataset.workerUrl || '').replace(/\/$/, '');
        this.rooms = [];
        this.productCount = 0;
        this.shareIds = [];
      }

      connectedCallback() {
        this.init();
      }

      async init() {
        // Check for shared wishlist ID in URL
        const urlParams = new URLSearchParams(window.location.search);
        const sharedListId = urlParams.get('list');
        const sharedRoomIds = (urlParams.get('rooms') || '').split(',').map((id) => id.trim()).filter((id) => /^[A-Za-z0-9]{4,32}$/.test(id)).slice(0, 20);
        this.isSharedView = !!(sharedListId || sharedRoomIds.length);

        this.rooms = await (this.isSharedView ? this.loadSharedRooms(sharedRoomIds) : this.loadOwnRooms());
        this.renderRooms();

        // A shared link may carry only room previews, with no Swish list behind it.
        if (this.isSharedView && !sharedListId) {
          this.finishInit(null, []);
          return;
        }

        // Wait for WishlistKing to load
        const wishlistKing = await this.getSwishInstance();

        if (!wishlistKing) {
          console.warn('[Wishlist] WishlistKing not available');
          this.finishInit(null, []);
          return;
        }

        try {
          // Determine which wishlist to load
          const wishlistIdToLoad = sharedListId || 'mine';

          // Debug: Loading wishlist (removed console.log)

          // Load wishlist using WishlistKing internal API
          const { wishlist } = await wishlistKing._apis.wishlist.loadWishlist({
            wishlistId: wishlistIdToLoad,
            cacheStrategy: 'no-store'
          });

          // Debug: Wishlist loaded (removed console.log)

          // Store the wishlist ID for sharing (publicId is the shareable ID)
          this.wishlistId = wishlist?.publicId || wishlist?.id || wishlist?._id || sharedListId;
          // Debug: Wishlist ID for sharing (removed console.log)

          await this.finishInit(wishlistKing, wishlist?.items || []);
        } catch (error) {
          console.error('[Wishlist] Error loading items:', error);
          this.finishInit(null, []);
        }
      }

      async finishInit(wishlistKing, items) {
        if (this.isSharedView) this.updateHeadingForSharedView();

        if (items.length) {
          const productVariantMap = this.buildProductVariantMap(items);
          await this.renderCards(Object.keys(productVariantMap), productVariantMap);
        } else {
          this.gridEl?.classList.add('hidden');
        }
        this.productCount = items.length;
        this.syncState();

        if (this.isSharedView) {
          if (this.shareBtn) this.shareBtn.style.display = 'none';
          return;
        }
        this.setupShareButton();
        this.prepareShareIds();
        if (wishlistKing) this.setupEventListeners(wishlistKing);
      }

      // Empty only when there is neither a saved product nor a room preview to show.
      syncState() {
        const total = this.productCount + this.rooms.length;
        this.loadingEl?.classList.add('hidden');
        if (!total) { this.showEmpty(); return; }
        this.emptyEl?.classList.add('hidden');
        this.updateCount(total);
        if (this.shareBtn && !this.isSharedView) this.shareBtn.hidden = false;
      }

      // The shopper's own previews come from the room scripts, which load only for the test
      // group; anyone outside it has none to show. This is the drawer's list too, including
      // previews that followed a signed-in shopper from another device.
      async loadOwnRooms() {
        const studio = await this.waitFor(() => window.LampsRoomStudio?.projects);
        if (!studio) return [];
        try {
          const all = await studio.projects();
          return all.filter((record) => record?.product).map((record) => ({
            image: record.blob ? URL.createObjectURL(record.blob) : record.imageUrl,
            title: record.product.title,
            url: record.product.url,
            record,
          })).filter((room) => room.image);
        } catch {
          return [];
        }
      }

      // A recipient may be in either Shoplift group, so shared previews are read straight
      // from the worker rather than through the room scripts.
      async loadSharedRooms(ids) {
        if (!this.workerUrl || !ids.length) return [];
        const rootUrl = window.Shopify?.routes?.root || '/';
        const settled = await Promise.allSettled(ids.map(async (id) => {
          const shareResponse = await fetch(`${this.workerUrl}/share/${encodeURIComponent(id)}`, { credentials: 'omit' });
          if (!shareResponse.ok) throw new Error('missing');
          const share = await shareResponse.json();
          const image = new URL(share.image_url, this.workerUrl);
          if (image.origin !== new URL(this.workerUrl).origin) throw new Error('foreign_image');
          const productResponse = await fetch(`${rootUrl}products/${encodeURIComponent(share.product_handle)}.js`);
          if (!productResponse.ok) throw new Error('no_product');
          const product = await productResponse.json();
          return { image: image.href, title: product.title, url: `${rootUrl}pages/room-preview?r=${encodeURIComponent(id)}` };
        }));
        return settled.filter((result) => result.status === 'fulfilled').map((result) => result.value);
      }

      waitFor(get, attempts = 20) {
        return new Promise((resolve) => {
          const check = (left) => {
            const found = get();
            if (found) return resolve(window.LampsRoomStudio);
            if (left <= 0) return resolve(null);
            setTimeout(() => check(left - 1), 250);
          };
          check(attempts);
        });
      }

      renderRooms() {
        if (!this.roomsEl || !this.roomsGrid) return;
        this.roomsGrid.replaceChildren();
        if (!this.rooms.length) { this.roomsEl.hidden = true; return; }
        this.querySelector('[data-wishlist-rooms-heading]').textContent =
          this.isSharedView ? this.dataset.sharedRoomsHeading : this.dataset.roomsHeading;
        for (const room of this.rooms) {
          const item = document.createElement('li');
          item.className = 'wishlist-rooms__item';
          const link = document.createElement('a');
          link.className = 'wishlist-rooms__link';
          link.href = room.url;
          const media = document.createElement('span');
          media.className = 'wishlist-rooms__media';
          const img = document.createElement('img');
          img.src = room.image;
          img.alt = room.title;
          img.loading = 'lazy';
          const badge = document.createElement('span');
          badge.className = 'wishlist-rooms__badge';
          badge.textContent = this.dataset.roomBadge;
          media.append(img, badge);
          const title = document.createElement('span');
          title.className = 'wishlist-rooms__title';
          title.textContent = room.title;
          const cta = document.createElement('span');
          cta.className = 'wishlist-rooms__cta';
          cta.textContent = this.dataset.viewProductLabel;
          link.append(media, title, cta);
          item.append(link);
          this.roomsGrid.append(item);
        }
        this.roomsEl.hidden = false;
      }

      // Minted when the page loads rather than on the click: the worker round trip would
      // otherwise spend the click's activation, and navigator.share would refuse.
      prepareShareIds() {
        const records = this.rooms.map((room) => room.record).filter(Boolean);
        if (!records.length || !window.LampsRoomShare?.projectShareIds) return;
        window.LampsRoomShare.projectShareIds(records).then((ids) => { this.shareIds = ids; }).catch(() => {});
      }

      updateHeadingForSharedView() {
        const heading = this.querySelector('.wishlist-header__title');
        if (heading) {
          heading.textContent = 'Shared projects';
        }
      }

      async getSwishInstance() {
        // Wait for WishlistKing to be available
        return new Promise((resolve) => {
          let attempts = 0;
          const maxAttempts = 20;

          const checkWishlistKing = () => {
            attempts++;

            if (window.WishlistKing?._apis?.wishlist) {
              // Debug: Found WishlistKing API (removed console.log)
              resolve(window.WishlistKing);
              return;
            }

            if (attempts < maxAttempts) {
              setTimeout(checkWishlistKing, 250);
            } else {
              console.warn('[Wishlist] WishlistKing not found after waiting');
              resolve(null);
            }
          };

          checkWishlistKing();
        });
      }

      buildProductVariantMap(items) {
        const map = {};
        for (const item of items) {
          // WishlistKing structure: item.product.id for product ID, item.variantId for variant
          const productId = item.product?.id || item.productId || item.product_id;
          const variantId = item.variantId || item.variant_id;
          if (productId) {
            // Store the variant ID for each product (use first if multiple)
            if (!map[productId]) {
              map[productId] = variantId;
            }
          }
        }
        return map;
      }

      async renderCards(productIds, productVariantMap) {
        if (productIds.length === 0) return;

        // Build search query for products by ID (same as recently-viewed pattern)
        const queryParams = productIds.map(id => 'id:' + id).join(' OR ');
        const rootUrl = window.Shopify?.routes?.root || '/';
        const searchUrl = `${rootUrl}search?section_id=wishlist-cards&type=product&q=${encodeURIComponent(queryParams)}`;

        try {
          const response = await fetch(searchUrl);
          const html = await response.text();

          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const cardsContainer = doc.querySelector('[data-wishlist-cards]');

          if (cardsContainer && cardsContainer.innerHTML.trim()) {
            // Update card links to include variant param
            const cards = cardsContainer.querySelectorAll('.f-column');
            cards.forEach(card => {
              const productCard = card.querySelector('.product-card');
              if (productCard) {
                const productId = this.extractProductId(card, productCard);
                const variantId = productVariantMap[productId];
                if (variantId) {
                  this.updateCardLinks(card, variantId);
                }
              }
            });

            this.gridEl.innerHTML = cardsContainer.innerHTML;
            this.showGrid();

            // Dispatch event for other components
            this.dispatchEvent(new CustomEvent('wishlist:loaded', {
              detail: { count: productIds.length }
            }));
          }
        } catch (error) {
          console.error('[Wishlist] Error fetching cards:', error);
        }
      }

      extractProductId(card, _productCard) {
        // Try to get product ID from card data attributes or form
        const form = card.querySelector('[data-type="add-to-cart-form"]');
        if (form) {
          const formId = form.getAttribute('id') || '';
          const match = formId.match(/(\d+)$/);
          if (match) return match[1];
        }

        // Try from quickview button
        const quickViewBtn = card.querySelector('[aria-controls*="QuickView-"]');
        if (quickViewBtn) {
          const controlId = quickViewBtn.getAttribute('aria-controls') || '';
          const match = controlId.match(/QuickView-(\d+)/);
          if (match) return match[1];
        }

        return null;
      }

      updateCardLinks(card, variantId) {
        // Update all product links to include variant parameter
        const links = card.querySelectorAll('a[href*="/products/"]');
        links.forEach(link => {
          const url = new URL(link.href, window.location.origin);
          url.searchParams.set('variant', variantId);
          link.href = url.toString();
        });
      }

      setupShareButton() {
        if (!this.shareBtn) return;

        this.shareBtn.addEventListener('click', async () => {
          // Generate shareable URL with wishlist ID
          const shareUrl = this.getShareUrl();
          // Debug: Share URL (removed console.log)

          // Check if on mobile (use native share) or desktop (use clipboard)
          const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

          const shareData = {
            title: 'My Projects',
            text: 'Lights I have saved to a project',
            url: shareUrl
          };

          try {
            // Use native share on mobile only
            if (isMobile && navigator.share && navigator.canShare?.(shareData)) {
              await navigator.share(shareData);
            } else {
              // Desktop: just copy to clipboard
              await navigator.clipboard.writeText(shareUrl);
              this.showShareFeedback('Link copied');
            }
          } catch (error) {
            if (error.name !== 'AbortError') {
              console.error('[Wishlist] Share error:', error);
              // Final fallback
              try {
                await navigator.clipboard.writeText(shareUrl);
                this.showShareFeedback('Link copied');
              } catch (e) {
                console.error('[Wishlist] Clipboard error:', e);
              }
            }
          }
        });
      }

      getShareUrl() {
        const url = new URL(window.location.href);
        url.searchParams.delete('list');
        url.searchParams.delete('rooms');
        if (this.wishlistId) url.searchParams.set('list', this.wishlistId);
        if (this.shareIds.length) url.searchParams.set('rooms', this.shareIds.join(','));
        return url.toString();
      }

      showShareFeedback(message) {
        // Simple feedback - could be enhanced with toast notification
        const originalText = this.shareBtn.querySelector('span:last-child');
        if (originalText) {
          const original = originalText.textContent;
          originalText.textContent = message;
          setTimeout(() => {
            originalText.textContent = original;
          }, 2000);
        }
      }

      setupEventListeners(wishlistKing) {
        // Listen for wishlist changes
        document.addEventListener('wlk:wishlist:updated', () => this.refresh(wishlistKing));
        document.addEventListener('wlk:wishlist:removed', () => this.refresh(wishlistKing));
      }

      async refresh(wishlistKing) {
        try {
          const { wishlist } = await wishlistKing._apis.wishlist.loadWishlist({
            wishlistId: 'mine',
            cacheStrategy: 'no-store'
          });

          const items = wishlist?.items || [];
          this.productCount = items.length;
          if (items.length) {
            const productVariantMap = this.buildProductVariantMap(items);
            await this.renderCards(Object.keys(productVariantMap), productVariantMap);
          } else {
            this.gridEl?.classList.add('hidden');
          }
          this.syncState();
        } catch (error) {
          console.error('[Wishlist] Refresh error:', error);
        }
      }

      updateCount(count) {
        if (this.countEl) {
          const numberEl = this.countEl.querySelector('.wishlist-count-number');
          if (numberEl) {
            numberEl.textContent = count;
          }
          // Update singular/plural
          const itemText = count === 1 ? 'item' : 'items';
          this.countEl.innerHTML = `<span class="wishlist-count-number">${count}</span> ${itemText}`;
        }
      }

      showLoading() {
        this.loadingEl?.classList.remove('hidden');
        this.emptyEl?.classList.add('hidden');
        this.gridEl?.classList.add('hidden');
      }

      showEmpty() {
        this.loadingEl?.classList.add('hidden');
        this.emptyEl?.classList.remove('hidden');
        this.gridEl?.classList.add('hidden');
        this.updateCount(0);
      }

      showGrid() {
        this.loadingEl?.classList.add('hidden');
        this.emptyEl?.classList.add('hidden');
        this.gridEl?.classList.remove('hidden');
      }
    }
  );
}
